import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sagaApi, type SagaDashboard, type SagaHistoryEntry, type SagaInstance } from '../../api/saga';

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  compensating: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  expired: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  cancelled: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
};

export function SagaDashboardPage() {
  const [dashboard, setDashboard] = useState<SagaDashboard | null>(null);
  const [sagas, setSagas] = useState<SagaInstance[]>([]);
  const [history, setHistory] = useState<SagaHistoryEntry[]>([]);
  const [selected, setSelected] = useState<SagaInstance | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function refresh() {
    try {
      const [dash, list, hist] = await Promise.all([
        sagaApi.dashboard(),
        sagaApi.list({ limit: 50 }),
        sagaApi.history(50),
      ]);
      setDashboard(dash);
      setSagas(list.sagas ?? []);
      setHistory(hist.history ?? []);
    } catch (e) {
      setError(String(e));
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function selectSaga(id: string) {
    try {
      const detail = await sagaApi.get(id);
      setSelected(detail.saga);
    } catch (e) {
      setError(String(e));
    }
  }

  async function action(kind: 'resume' | 'cancel' | 'retry', id: string) {
    setLoading(true);
    setError('');
    try {
      if (kind === 'resume') await sagaApi.resume(id);
      if (kind === 'cancel') await sagaApi.cancel(id);
      if (kind === 'retry') await sagaApi.retry(id);
      await refresh();
      await selectSaga(id);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  const m = dashboard?.metrics;

  return (
    <div className="mx-auto max-w-7xl p-6">
      <Link to="/workflows/center" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
        ← Workflow Center
      </Link>
      <h1 className="mt-2 text-2xl font-semibold">Saga Dashboard</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000059 — persistent saga engine, recovery console, compensation tracking and workflow timeline.
      </p>
      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ['Total', m?.total_instances ?? 0],
          ['Running', m?.running ?? 0],
          ['Completed', m?.completed ?? 0],
          ['Failed', m?.failed ?? 0],
          ['Recovery', m?.recovery_pending ?? 0],
        ].map(([label, val]) => (
          <div key={String(label)} className="rounded-xl border p-4 dark:border-slate-700">
            <p className="text-sm text-slate-500">{label}</p>
            <p className="text-2xl font-semibold">{val}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border dark:border-slate-700">
          <div className="border-b px-4 py-3 dark:border-slate-700">
            <h2 className="font-medium">Saga Instances</h2>
          </div>
          <ul className="divide-y dark:divide-slate-700">
            {sagas.length === 0 && (
              <li className="px-4 py-6 text-sm text-slate-500">No sagas yet — execute a process from Workflow Center.</li>
            )}
            {sagas.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-4 py-3">
                <button type="button" onClick={() => void selectSaga(s.id)} className="text-left">
                  <p className="font-medium">{s.process_id}</p>
                  <p className="text-xs text-slate-500">{s.id.slice(0, 8)}… · step {s.current_step}/{s.steps.length}</p>
                </button>
                <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[s.status] ?? 'bg-slate-100'}`}>
                  {s.status}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border dark:border-slate-700">
          <div className="border-b px-4 py-3 dark:border-slate-700">
            <h2 className="font-medium">{selected ? 'Execution Viewer' : 'Select a saga'}</h2>
          </div>
          {selected ? (
            <div className="space-y-4 p-4">
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={loading} onClick={() => void action('resume', selected.id)}
                  className="rounded-lg bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50">Resume</button>
                <button type="button" disabled={loading} onClick={() => void action('retry', selected.id)}
                  className="rounded-lg border px-3 py-1 text-sm dark:border-slate-600 disabled:opacity-50">Retry</button>
                <button type="button" disabled={loading} onClick={() => void action('cancel', selected.id)}
                  className="rounded-lg border px-3 py-1 text-sm text-red-600 dark:border-slate-600 disabled:opacity-50">Cancel</button>
              </div>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">Correlation</dt><dd>{selected.correlation_id}</dd>
                <dt className="text-slate-500">Trace</dt><dd>{selected.trace_id || '—'}</dd>
                <dt className="text-slate-500">Checkpoint</dt><dd>{selected.checkpoint_step}</dd>
              </dl>
              <ol className="space-y-2">
                {selected.steps.map((step, i) => (
                  <li key={step.id} className="flex items-center gap-2 text-sm">
                    <span className="w-6 text-slate-400">{i + 1}.</span>
                    <span className="flex-1">{step.name}</span>
                    <span className={`rounded px-1.5 text-xs ${STATUS_COLORS[step.status] ?? ''}`}>{step.status}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <p className="p-4 text-sm text-slate-500">Click a saga to view timeline, checkpoints and recovery actions.</p>
          )}
        </section>
      </div>

      <section className="mt-8 rounded-xl border dark:border-slate-700">
        <div className="border-b px-4 py-3 dark:border-slate-700">
          <h2 className="font-medium">Durable History</h2>
        </div>
        <ul className="max-h-64 divide-y overflow-y-auto dark:divide-slate-700">
          {history.map((h) => (
            <li key={h.id} className="px-4 py-2 text-sm">
              <span className="font-mono text-xs text-slate-500">{h.saga_id.slice(0, 8)}</span>
              {' · '}{h.action}
              {h.to_state && <> → <span className="font-medium">{h.to_state}</span></>}
              {h.details && <span className="text-slate-500"> — {h.details}</span>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
